/**
 * Persistence for user-imported ranking sources (BYOR) across reloads.
 *
 * Built-in sources (Sleeper, FFC ADP, FantasyCalc) are not persisted here —
 * they refetch from the network on every load, and a stale copy of "the
 * market" would silently mislead. A pasted list is different: it is the
 * user's own authored content, produced nowhere else, and losing it on every
 * refresh meant re-pasting it every session. Persisting `kind: 'user'`
 * sources is the fix; there is no TTL because deleting someone's own
 * rankings on a timer is a worse surprise than a staleness warning
* (`stalenessWarnings`, keyed off `as_of_date`, already covers that).
 *
 * Guest copies stay device-local; account copies use a separate per-user
 * namespace and are synchronized only when the user opts in.
 *
 * Lives in IndexedDB on the user's device only (FR-64) — same store as the
 * player cache, so `clearAll()` already wipes it with everything else.
 */

import type { RankingSource } from '../core/types.ts';
import { idbGet, idbSet, scopedIdbGet, scopedIdbSet } from './storage.ts';

const KEY = 'user_sources';

export async function loadUserSources(): Promise<RankingSource[]> {
  const stored = await idbGet<RankingSource[]>(KEY);
  return stored ?? [];
}

export async function loadAccountSources(userId: string): Promise<RankingSource[]> {
  const stored = await scopedIdbGet<RankingSource[]>(userId, KEY);
  return stored ?? [];
}

export function saveUserSources(sources: RankingSource[]): Promise<boolean> {
  return idbSet(
    KEY,
    sources.filter((s) => s.kind === 'user'),
    null,
  );
}

export function saveAccountSources(userId: string, sources: RankingSource[]): Promise<boolean> {
  return scopedIdbSet(
    userId,
    KEY,
    sources.filter((s) => s.kind === 'user'),
    null,
  );
}
